import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  Navigate,
} from "react-router-dom";

import LoginPage from "./pages/LoginPage";
import Home from "./pages/Home";
import DashboardPage from "./pages/DashboardPage";
import AttendancePage from "./pages/AttendancePage";
import AdminAttendanceOverview from "./pages/AdminAttendanceOverview";
import AdminLeaveApplications from "./pages/LeaveApplications";
import AdminLeaveReports from "./pages/LeaveReports";
import AdminExpenseApproval from "./pages/AdminExpenseApproval";
import AdminProducts from "./pages/AdminProducts";
import AdminEmployees from "./pages/AdminUserManagement";
import SalaryGenerate from "./pages/SalaryGenerate";
import TaskManagement from "./pages/TaskManagement";
import EmployeeTaskPage from "./pages/EmployeeTask";
import StatisticsPage from "./pages/Statistics";
import SupportTicketPage from "./pages/SupportTicketPage";
import AdminReports from "./pages/AdminReportPage";
import KRASystem from "./pages/KRADashboard";

/* ---------------- APP ROOT ---------------- */
const App = () => (
  <Router>
    <MainApp />
  </Router>
);



/* ---------------- PROTECTED ROUTE ---------------- */
const ProtectedRoute = ({ isLoggedIn, children }) => {
  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

/* ---------------- MAIN APP ---------------- */
const MainApp = () => {
  const navigate = useNavigate();

  // 🔐 login state persisted
  const [isLoggedIn, setIsLoggedIn] = useState(
    sessionStorage.getItem("isLoggedIn") === "true"
  );

  /* -------- KEEP LOGIN AFTER REFRESH -------- */
  useEffect(() => {
    const loggedIn = sessionStorage.getItem("isLoggedIn") === "true";
    setIsLoggedIn(loggedIn);
  }, []);

  /* -------- LOGIN SUCCESS -------- */
  const handleLoginSuccess = () => {
    sessionStorage.setItem("isLoggedIn", "true");
    setIsLoggedIn(true);
    navigate("/attendance", { replace: true });
  };

  /* -------- LOGOUT -------- */
  const handleLogout = () => {
    sessionStorage.clear();        // 🔥 kills login immediately
    localStorage.removeItem("employee"); // optional
    localStorage.removeItem("user");     // optional

    setIsLoggedIn(false);

    // 🔐 Prevent browser back navigation
    window.history.replaceState(null, "", "/login");
    navigate("/login", { replace: true });
  };

  return (


    <Routes>
      {/* PUBLIC */}
      <Route
        path="/login"
        element={<LoginPage onLoginSuccess={handleLoginSuccess} />}
      />
      <Route
        path="/"
        element={
          isLoggedIn ? (
            <Navigate to="/attendance" replace />
          ) : (
            <LoginPage onLoginSuccess={handleLoginSuccess} />
          )
        }
      />

      {/* PROTECTED ROUTES */}
      <Route
        path="/home"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <Home onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/attendance"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <AttendancePage onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/lead"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <DashboardPage onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/assignments"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <EmployeeTaskPage onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/ticketing"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <SupportTicketPage onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />


      <Route
        path="/kra-dashboard"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <KRASystem onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/attendance-overview"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <AdminAttendanceOverview onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/leave-overview"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <AdminLeaveApplications onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/leave-reports"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <AdminLeaveReports onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/assign-tasks"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <TaskManagement onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/lead-overview"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <DashboardPage onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/expense-history"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <AdminExpenseApproval onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/add-product"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <AdminProducts onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/statistics"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <StatisticsPage onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <AdminReports onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/add-employee"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <AdminEmployees onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/generate-payslip"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <SalaryGenerate onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      {/* FALLBACK */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

export default App;